import { Button, Form, Radio, Space, Table } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { useAddEmployeeMutation, useGetCompaniesQuery, useRemoveEmployeeMutation } from '../../api/company.api'
import { useGetEventUserQuery, useUpdateEventUserMutation } from '../../api/eventUser.api'
import FormBuilder from '../../components/FormBuilder'
import { useMyContext } from '../../context/context'
import useConf from '../../hooks/useConf'
import useSettingsV2 from '../../hooks/useSettingsV2'
import useTranslations from '../../hooks/useTranslations'
import { useSteps } from '../steps/Steps'
import HTML from '../../components/HTML'

const CompaniesList = ({ toSiren, toCreate, companyId }) => {
  const [selected, setSelected] = useState()

  const myContext = useMyContext()
  const { nextStep, userId: userId2, NextButton, CompanionName, participation } = useSteps()
  const { companyIndexLabel } = useConf(participation)
  const [formRef] = Form.useForm()
  const { settings: settingsV2 } = useSettingsV2()
  const { t } = useTranslations()

  const { domainId, userId } = myContext

  const { data: companies } = useGetCompaniesQuery({ domainId, userId })
  const { data: user } = useGetEventUserQuery({ ...myContext, userId: userId2 })
  const [updateUserEvent] = useUpdateEventUserMutation()
  const [addEmployee] = useAddEmployeeMutation()
  const [removeEmployee] = useRemoveEmployeeMutation()

  const companySelected = useMemo(() => companies?.find(f => f.id === selected), [companies, selected])

  useEffect(() => {
    if (user && companies?.length > 0) {
      if (companyId) {
        const find = companies.find(f => f.id === companyId)
        if (find) {
          setSelected(companyId)
          return
        }
      }

      const find = companies.find(f => f.id === user?.UserEvent?.Employee?.Company?.id)
      if (find) {
        setSelected(find.id)
      }
    } else {
      setSelected(null)
    }
  }, [companies, companyId, user])

  useEffect(() => {
    if (companies?.length === 1) {
      setSelected(companies[0].id)
    }
  }, [companies])

  return (
    <div>
      <HTML name="FO_MEETING_COMPANIES_HTML" />
      {CompanionName}
      <Button type="primary" onClick={toSiren}>
        {t('ADD_COMPANY_TO_LIST')}
      </Button>
      <Table
        rowKey="id"
        dataSource={companies}
        size="small"
        onRow={v => {
          return { onClick: () => setSelected(v.id) }
        }}
        scroll={{ x: true }}
        locale={{ emptyText: t('COMPANIES_EMPTY') }}
        columns={[
          {
            title: t('COMPANIES_SELECT'),
            render: company => (
              <Space>
                <Button type="primary" onClick={() => removeEmployee({ ...myContext, companyId: company.id })} size="small">
                  {t('REMOVE')}
                </Button>
              </Space>
            ),
          },
          { title: t('COMPANY_NAME'), dataIndex: 'name' },
          { title: companyIndexLabel || t('COMPANY_INDEX'), dataIndex: 'siren' },
          { title: t('COMPANY_ADDRESS_1'), dataIndex: 'address1' },
          { title: t('COMPANY_POSTAL_CODE'), dataIndex: 'postalCode' },
          { title: t('COMPANY_CITY'), dataIndex: 'city' },
          { render: v => <Radio checked={v.id === selected} onChange={() => setSelected(v.id)} /> },
          {
            title: '',
            render: company => (
              <Space>
                <Button type="primary" onClick={() => toCreate(company)} size="small">
                  {t('EDIT')}
                </Button>
              </Space>
            ),
          },
        ]}
      />
      {settingsV2?.FO_MEETING_CREATE_COMPANY_FORM_ID && companySelected && (
        <div style={{ display: 'none' }}>
          <FormBuilder formRef={formRef} formIdDefault={settingsV2.settingsV2} companyId={companySelected.id} />
        </div>
      )}
      <NextButton
        onClick={async () => {
          const company = companies?.find(f => f.id === selected)
          const isValid = await formRef
            .validateFields()
            .then(() => true)
            .catch(() => false)

          if (!isValid || !company.siren) {
            toCreate(company)
          } else {
            const employee = await addEmployee({ ...myContext, userId: userId2, companyId: selected }).unwrap()
            await updateUserEvent({ ...myContext, userId: userId2, eventUser: { employeeId: employee.id } })
            nextStep()
          }
        }}
        disabled={!selected}
      />
    </div>
  )
}

export default CompaniesList
